<script>
  /* global Global */
  import Header from '../common/Header.svelte';
  import Footer from '../common/Footer.svelte';
  import Loading from '../common/Loading.svelte';
  import Error from '../common/Error.svelte';
  import Title from '../common/Title.svelte';
  import CountdownOrLive from '../ui-components/CountdownOrLive.svelte';
  import ImportantLink from '../ui-components/ImportantLink.svelte';
  import Button from '../common/Button.svelte';
  import TweetsBox from '../ui-components/home/TweetsBox.svelte';
  import ArticlesBox from '../ui-components/home/ArticlesBox.svelte';
  import LinksBox from '../ui-components/home/LinksBox.svelte';
  import PartnersBox from '../ui-components/home/PartnersBox.svelte';
  import PageBox from '../ui-components/PageBox.svelte';
  import Common from '../js/common.js';

  let lang;
  let mode;

  let user = JSON.parse(window.localStorage.getItem('user'));

  let pinnedArticle;
  let articles = [];
  let tweets = [];
  let importantLinks = [];
  let links = [];
  let partners = [];
  let page;

  let loading = true;
  let error = false;

  (async () => {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${Common.BACKEND_URL}/home/get`);

    if (response.ok) {
      const data = await response.json();

      pinnedArticle = data.pinnedArticle || undefined;
      articles = data.articles;
      tweets = data.tweets;
      importantLinks = data.importantLinks;
      links = data.links;
      partners = data.partners;
      page = data.page;
      user = data.user;

      articles = Common.sortArrayOfObjectsByKey(articles, ['date'], true);
      tweets = Common.sortArrayOfObjectsByKey(tweets, ['timestamp'], true);

      if (user) window.localStorage.setItem('user', JSON.stringify(data.user));
      else window.localStorage.removeItem('user');
    } else {
      error = true;
    }

    loading = false;
  })();

  const i18n = {
    pageTitle: {
      cs: `Domů | ${Global.project.name.cs}`,
      en: `Home | ${Global.project.name.en}`,
    },
    showAll: {
      cs: 'Zobrazit všechny články',
      en: 'Show all articles',
    },
    addArticle: {
      cs: 'Přidat článek',
      en: 'Add article',
    },
    addTweet: {
      cs: 'Přidat tweet',
      en: 'Add tweet',
    },
    editImportantLinks: {
      cs: 'Upravit důležité odkazy',
      en: 'Edit important links',
    },
    editLinks: {
      cs: 'Upravit odkazy',
      en: 'Edit links',
    },
    editPartners: {
      cs: 'Upravit partnery',
      en: 'Edit partners',
    },
  };

  const dateFrom = Global.date.from;
  const dateTo = Global.date.to;
  const radiozurnal = Global.home.radiozurnal;
</script>

<svelte:head>
  <title> {i18n.pageTitle[lang]} </title>
</svelte:head>

<Header page='home' bind:lang bind:user bind:mode/>

<Title subtitle='{dateFrom} – {dateTo}' home={true} bind:lang bind:user/>

<CountdownOrLive bind:lang/>

<div class='home' class:home-loading-or-error={loading || error}>
  <Loading bind:display={loading} home={true}/>
  <Error bind:display={error} home={true} bind:lang/>

  {#if !loading && !error}
    {#if importantLinks.length > 0}
      <div class='home-important-link'>
        <ImportantLink value={importantLinks[0]} bind:lang/>
      </div>
    {/if}

    <div class='home-content'>
      <div class='home-content-tweets-and-articles'>
        {#if user}
          <div class='home-content-tweets-and-articles-admin'>
            <Button value={i18n.addArticle[lang]} icon='add' admin={true}
              onClick={() => { window.location.href = './#/admin/articles/add'; }}/>

            <Button value={i18n.addTweet[lang]} icon='add' admin={true}
              onClick={() => { window.location.href = './#/admin/tweets/add'; }}/>
          </div>
        {/if}

        <div class='home-content-tweets-and-articles-box'>
          <TweetsBox tweets={tweets} bind:lang admin={!!user}/>

          <ArticlesBox pinnedArticle={pinnedArticle} articles={articles} bind:lang admin={!!user}/>
        </div>
      </div>

      <div class='home-content-links'>
        {#if user}
          <div class='home-content-links-admin-important-links'>
            <Button value={i18n.editImportantLinks[lang]} icon='edit' admin={true} disabled={!user.roles.includes('ROLE_ADMIN')}
              onClick={() => { window.location.href = './#/admin/important-links'; }}/>
          </div>
        {/if}

        {#if importantLinks.length > 0 || links.length > 0}
          <div class='home-content-links-box'>
            <LinksBox importantLinks={importantLinks} links={links} bind:lang/>
          </div>
        {/if}

        {#if user}
          <div class='home-content-links-admin-links'>
            <Button value={i18n.editLinks[lang]} icon='edit' admin={true} disabled={!user.roles.includes('ROLE_ADMIN')}
              onClick={() => { window.location.href = './#/admin/links'; }}/>
          </div>
        {/if}

        {#if radiozurnal}
          <iframe class='home-content-links-radiozurnal' src='https://sport.rozhlas.cz/cro_soundmanager/rz_sport/embed' title='Radiožurnál sport' frameborder='0' allowfullscreen></iframe>
        {/if}
      </div>
    </div>

    {#if partners.length > 0 || user}
      <div class='home-partners'>
        <PartnersBox partners={partners} bind:lang bind:mode/>

        {#if user}
          <div class='home-partners-admin'>
            <Button value={i18n.editPartners[lang]} icon='edit' admin={true} disabled={!user.roles.includes('ROLE_ADMIN')}
              onClick={() => { window.location.href = './#/admin/partners'; }}/>
          </div>
        {/if}
      </div>
    {/if}

    <PageBox value={page} page='home' bind:lang bind:user/>
  {/if}
</div>

<Footer bind:lang/>

<style>
  .home {
    display: flex;
    flex-direction: column;
    gap: 40px;

    max-width: 1450px;

    padding: 40px 60px;

    margin: auto;
  }

  .home-loading-or-error {
    padding: 30px 60px;
  }

  .home-important-link {
    display: none;
  }

  .home-content {
    display: flex;
    justify-content: space-between;
    gap: 40px;
  }

  .home-content-tweets-and-articles {
    display: flex;
    flex-direction: column;
    gap: 40px;

    width: calc((100% - 40px) / 3 * 2);
  }

  .home-content-tweets-and-articles-admin {
    display: flex;
    gap: 20px;
  }

  .home-content-tweets-and-articles-box {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  .home-content-links {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 40px;

    width: calc((100% - 40px) / 3);
  }

  .home-content-links-radiozurnal {
    border-radius: var(--radius-lg);
  }

  .home-partners {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  @media only screen and (max-width: 1300px) {
    .home-important-link {
      display: block;
    }

    .home-content {
      flex-direction: column;
    }

    .home-content-tweets-and-articles {
      width: 100%;
    }

    .home-content-links {
      width: 100%;
    }
  }

  @media only screen and (max-width: 900px) {
    .home {
      padding: 40px;
    }

    .home-loading-or-error {
      padding: 30px 40px;
    }
  }

  @media only screen and (max-width: 750px) {
    .home-content-tweets-and-articles-admin {
      flex-direction: column;
    }
  }

  @media only screen and (max-width: 350px) {
    .home {
      padding: 40px 20px;
    }

    .home-loading-or-error {
      padding: 30px 20px;
    }
  }
</style>
